/**
 * Top-down "panorámica" 3D-style padel court rendering with SVG.
 * Realistic real-world proportion 20m x 10m, viewed from slightly above.
 * Pure CSS/SVG, no three.js.
 *
 * Props:
 *   name: string
 *   size: number — width in px (height auto)
 *   className, testId
 */
export default function PadelCourt3D({ name = "Cancha", size = 420, className = "", testId = "padel-court-3d" }) {
  const W = size;
  const H = Math.round(size * 0.55); // top-down panorámica ratio
  return (
    <div className={`relative overflow-hidden ${className}`} style={{ width: W, height: H }} data-testid={testId}>
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at top, #1A1F2E 0%, #060810 70%)" }} />
      <svg
        viewBox="0 0 600 330"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ position: "absolute", inset: 0 }}
        aria-label={name}
      >
        <defs>
          <linearGradient id="floor-blue" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0E5C9C" />
            <stop offset="50%" stopColor="#0A4878" />
            <stop offset="100%" stopColor="#062F4E" />
          </linearGradient>
          <linearGradient id="floor-side" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#062F4E" />
            <stop offset="100%" stopColor="#020D17" />
          </linearGradient>
          <linearGradient id="glass-top" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgba(180,220,240,0.45)" />
            <stop offset="100%" stopColor="rgba(80,120,150,0.10)" />
          </linearGradient>
          <linearGradient id="net-mtl" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#E2E2E6" />
            <stop offset="50%" stopColor="#A8A8AE" />
            <stop offset="100%" stopColor="#5C5C66" />
          </linearGradient>
          <pattern id="netmesh-h" patternUnits="userSpaceOnUse" width="6" height="6">
            <path d="M0,0 L6,6 M0,6 L6,0" stroke="#FFFFFF" strokeWidth="0.4" opacity="0.55" />
          </pattern>
          <radialGradient id="ball-grad" cx="50%" cy="40%" r="55%">
            <stop offset="0%" stopColor="#F4FF5A" />
            <stop offset="60%" stopColor="#D6F100" />
            <stop offset="100%" stopColor="#9ABC00" />
          </radialGradient>
          <linearGradient id="racket-1" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#1F1F1F" />
            <stop offset="100%" stopColor="#0A0A0A" />
          </linearGradient>
        </defs>

        {/* Outer shadow surround (depth) */}
        <ellipse cx="300" cy="290" rx="280" ry="20" fill="#000" opacity="0.6" />

        {/* Side dropshadow polygon for depth (slight perspective) */}
        <polygon points="40,75 560,75 580,265 20,265" fill="url(#floor-side)" opacity="0.95" />

        {/* Main floor (top-down with slight perspective) */}
        <polygon points="50,80 550,80 570,260 30,260" fill="url(#floor-blue)" />

        {/* Court boundary white lines */}
        <g stroke="#FFFFFF" strokeWidth="2.2" opacity="0.95" fill="none">
          <polygon points="50,80 550,80 570,260 30,260" />
          {/* center net line (across) */}
          <line x1="40" y1="170" x2="560" y2="170" />
          {/* service lines */}
          <line x1="135" y1="125" x2="465" y2="125" />
          <line x1="115" y1="215" x2="485" y2="215" />
          {/* center vertical */}
          <line x1="300" y1="125" x2="300" y2="215" />
        </g>

        {/* Side glass walls (top-down sliver) */}
        <polygon points="50,80 30,260 16,255 36,75" fill="url(#glass-top)" stroke="#9CB3C5" strokeWidth="0.6" opacity="0.7" />
        <polygon points="550,80 570,260 584,255 564,75" fill="url(#glass-top)" stroke="#9CB3C5" strokeWidth="0.6" opacity="0.7" />
        {/* Back glass walls */}
        <polygon points="50,80 550,80 564,75 36,75" fill="url(#glass-top)" stroke="#9CB3C5" strokeWidth="0.6" opacity="0.65" />
        <polygon points="30,260 570,260 584,265 16,265" fill="url(#glass-top)" stroke="#9CB3C5" strokeWidth="0.6" opacity="0.65" />

        {/* Metallic frames around walls */}
        <g stroke="#1F2733" strokeWidth="2.2" fill="none">
          <line x1="50" y1="80" x2="550" y2="80" />
          <line x1="30" y1="260" x2="570" y2="260" />
          <line x1="50" y1="80" x2="30" y2="260" />
          <line x1="550" y1="80" x2="570" y2="260" />
        </g>

        {/* Mid back-wall metal */}
        <line x1="36" y1="75" x2="564" y2="75" stroke="#0F1620" strokeWidth="3" />

        {/* Fence posts (stadium-style) */}
        <g fill="#1A1F2A">
          {[60, 175, 300, 425, 540].map((x, i) => (
            <rect key={i} x={x - 1.5} y="65" width="3" height="12" />
          ))}
        </g>

        {/* Net (center horizontal across full width) */}
        <g>
          <rect x="40" y="166" width="520" height="9" fill="url(#net-mtl)" opacity="0.95" />
          <rect x="40" y="166" width="520" height="9" fill="url(#netmesh-h)" />
          <rect x="40" y="164" width="520" height="2.5" fill="#FFFFFF" opacity="0.9" />
          {/* net posts on each side */}
          <rect x="38" y="160" width="4" height="20" fill="#1F1F1F" />
          <rect x="558" y="160" width="4" height="20" fill="#1F1F1F" />
        </g>

        {/* LED LIGHT bars on long sides (stadium feel) */}
        <g fill="#CCFF00" opacity="0.85">
          {[120, 220, 380, 480].map((x, i) => <rect key={i} x={x} y="62" width="60" height="2.5" />)}
          {[120, 220, 380, 480].map((x, i) => <rect key={`b-${i}`} x={x} y="266" width="60" height="2.5" />)}
        </g>

        {/* Two padel rackets (top-down with shadow) */}
        <g transform="translate(190,210) rotate(-30)">
          <ellipse cx="0" cy="0" rx="22" ry="14" fill="url(#racket-1)" stroke="#CCFF00" strokeWidth="1.4" />
          <ellipse cx="0" cy="0" rx="13" ry="7" fill="none" stroke="#CCFF00" strokeWidth="0.5" opacity="0.5" />
          <rect x="-2.5" y="13" width="5" height="22" rx="1" fill="#0A0A0A" stroke="#CCFF00" strokeWidth="0.5" />
        </g>
        <g transform="translate(410,140) rotate(20)">
          <ellipse cx="0" cy="0" rx="22" ry="14" fill="url(#racket-1)" stroke="#FF6B00" strokeWidth="1.4" />
          <ellipse cx="0" cy="0" rx="13" ry="7" fill="none" stroke="#FF6B00" strokeWidth="0.5" opacity="0.5" />
          <rect x="-2.5" y="13" width="5" height="22" rx="1" fill="#0A0A0A" stroke="#FF6B00" strokeWidth="0.5" />
        </g>

        {/* Pelota with glow */}
        <g>
          <circle cx="300" cy="178" r="9" fill="#000" opacity="0.4" />
          <circle cx="300" cy="172" r="6" fill="url(#ball-grad)" stroke="#FFFFFF" strokeWidth="0.5" />
          <path d="M 295,170 Q 300,167 305,170" stroke="#FFFFFF" strokeWidth="0.4" fill="none" opacity="0.7" />
        </g>

        {/* Court label */}
        <text x="300" y="305" textAnchor="middle" fill="#CCFF00" fontFamily="'JetBrains Mono', monospace" fontSize="11" fontWeight="bold" letterSpacing="3">
          {(name || "").toUpperCase()}
        </text>
      </svg>
    </div>
  );
}
